import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Lock, Eye, Database, UserCheck, Globe, Mail, Calendar } from 'lucide-react';
import { BspiFooter, BspiHeader } from './bspi/HomeSections';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const sectionIcons = {
  intro: Shield,
  info_collect: Database,
  info_use: Eye,
  data_security: Lock,
  your_rights: UserCheck,
  cookies: Globe
};

export const PrivacyPolicyPage = () => {
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Privacy Policy | Black Star PI';
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/privacy-policy`);
        if (res.ok) {
          const data = await res.json();
          setPageData(data);
        }
      } catch (e) {
        console.log('Failed to fetch privacy policy');
      }
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center">
        <div className="animate-pulse text-[#D4AF37]">Loading...</div>
      </div>
    );
  }

  if (!pageData?.enabled) {
    return (
      <div className="min-h-screen bg-[#0a1a14] flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 text-white/20 mx-auto mb-4" />
          <h1 className="text-2xl text-white/60 mb-4">Privacy Policy</h1>
          <p className="text-white/40 mb-6">This page is currently unavailable.</p>
          <Link to="/" className="text-[#D4AF37] hover:underline">Return to Homepage</Link>
        </div>
      </div>
    );
  }

  const companyName = pageData?.company_name || 'Black Star PI Ltd';
  const companyShort = pageData?.company_short || 'Black Star PI';
  const contactEmail = pageData?.contact_email || 'info@black-starpi.com';
  const sections = pageData?.sections || [];

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      {/* Site-wide header (BspiHeader) */}
      <div className="bspi chrome-only">
        <BspiHeader />
      </div>

      {/* Hero Section */}
      <section className="pt-10 pb-10 md:pt-12 md:pb-12 border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="w-16 h-16 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#D4AF37]" />
          </div>
          <h1 className="text-4xl md:text-5xl font-light text-white mb-6 tracking-wide">
            Privacy <span className="text-[#D4AF37]">Policy</span>
          </h1>
          <p className="text-lg text-white/50 max-w-2xl mx-auto leading-relaxed">
            Your privacy matters to us. This policy explains how {companyShort}{' '}
            collects, uses, and protects your personal information.
          </p>
          {pageData?.last_updated && (
            <p className="text-white/30 text-sm mt-6 flex items-center justify-center gap-2">
              <Calendar className="w-4 h-4" />
              Last updated: {new Date(pageData.last_updated).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}
        </div>
      </section>

      {/* Main Content - Dynamic Sections */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 space-y-12">
          {sections.map((section, idx) => {
            const IconComponent = sectionIcons[section.id] || Shield;
            return (
              <div key={section.id || idx} className="mb-12">
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 rounded-lg flex items-center justify-center">
                    <IconComponent className="w-6 h-6 text-[#D4AF37]" />
                  </div>
                  <h2 className="text-2xl text-[#D4AF37] font-light tracking-wide">{section.title}</h2>
                </div>
                <div className="p-6 bg-white/[0.02] border border-white/10 rounded-lg">
                  <p className="text-white/60 leading-relaxed whitespace-pre-line">{section.content}</p>
                </div>
              </div>
            );
          })}

          {/* Contact Section */}
          <div className="p-8 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-lg">
            <div className="flex items-start gap-6">
              <Mail className="w-10 h-10 text-[#D4AF37] flex-shrink-0" />
              <div>
                <h3 className="text-xl text-white font-medium mb-4">Contact Us</h3>
                <p className="text-white/50 leading-relaxed mb-4">
                  If you have any questions about this privacy policy or wish to exercise your rights, 
                  please contact our Data Protection Officer:
                </p>
                <a 
                  href={`mailto:${contactEmail}`}
                  className="inline-flex items-center gap-3 bg-[#D4AF37] text-black px-6 py-4 rounded-lg hover:bg-[#B8941E] transition-colors font-medium"
                >
                  <Mail className="w-5 h-5" />
                  {contactEmail}
                </a>
                <p className="text-white/40 text-sm mt-4">
                  You also have the right to lodge a complaint with the Information Commissioner's Office (ICO).
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer — shared v2 footer for consistency with the homepage */}
      <div className="bspi chrome-only">
        <BspiFooter />
      </div>
    </div>
  );
};

export default PrivacyPolicyPage;
